import {DashboardRepository} from "@services/repositories/dashboardRepository";
import {useRecoilValue} from "recoil";
import {brandState, shopState, spaceState} from "@services/stores/storeState";
import dayjs from "dayjs";
import {_SERVER_CONGESTION_TYPE, DATE_FORMAT_DASH, TIME_MIN_FORMAT} from "@app/constants/types";
import {RouteUrl} from "@app/constants/routeUrl";
import {useNavigate} from "react-router-dom";
import customParseFormat from "dayjs/plugin/customParseFormat";
import PageableViewModel from "@contents/viewModels/pageableViewModel";
import {useEffect, useMemo} from "react";
import {DiningOperationTimesDashboardEntity} from "@services/entities/dashboardEntity";

export const DashboardViewModel = () => {
    dayjs.extend(customParseFormat);
    const dashboardRepository = DashboardRepository();
    const spaceInfo = useRecoilValue(spaceState);
    const brandInfoState = useRecoilValue(brandState);
    const shopInfoState = useRecoilValue(shopState);

    const navigate = useNavigate();

    const noticePageable = PageableViewModel();
    const manualPageable = PageableViewModel();

    const cafeOperationData = dashboardRepository.cafeOperationDashboardData;
    const cafeManualData = dashboardRepository.cafeManualDashboardData;
    const cafeNoticeData = dashboardRepository.cafeNoticeDashboardData;
    const cafeOrderByTimeData = dashboardRepository.cafeOrderByTimeDashboardData?.content;
    const cafeDeviceData = dashboardRepository.cafeDeviceDashboardData;

    const diningManualData = dashboardRepository.diningManualDashboardData;
    const diningNoticeData = dashboardRepository.diningNoticeDashboardData;
    const diningDeviceData = dashboardRepository.diningDeviceDashboardData;
    const diningOrderByTimeData = dashboardRepository.diningOrderByTimeDashboardData?.content;
    const diningShopStatusData = dashboardRepository.diningShopStatusDashboardData;
    const diningOperationData = dashboardRepository.diningOperationDashboardData;
    const diningCourseStatusData = dashboardRepository.diningCourseStatusDashboardData;

    const brandUsageShopData = dashboardRepository.brandUsageShopDashboardData?.content;
    const brandUsageCompanyData = dashboardRepository.brandUsageCompanyDashboardData?.content;
    const brandNoticeData = dashboardRepository.brandNoticeDashboardData;
    const brandManualData = dashboardRepository.brandManualDashboardData;
    const brandRegisteredData = dashboardRepository.brandRegisteredDashboardData?.content;
    const brandRegisteredGraphData = dashboardRepository.brandRegisteredGraphDashboardData?.content;

    useEffect(() => {
        noticePageable?.onRowPerPage(10);
        manualPageable?.onRowPerPage(10);
    }, []);

    useEffect(() => {
        if (spaceInfo?.selectedOperation === 'CAFE') {
            getCafeNoticeDashboard();
        } else if (spaceInfo?.selectedOperation === 'DINING') {
            getDiningNoticeDashboard();
        } else if (spaceInfo?.selectedOperation === 'BRAND') {
            getBrandNoticeDashboard();
        }
    }, [noticePageable.rowPerPage, noticePageable.page, spaceInfo]);

    useEffect(() => {
        if (spaceInfo?.selectedOperation === 'CAFE') {
            getCafeManualDashboard();
        } else if (spaceInfo?.selectedOperation === 'DINING') {
            getDiningManualDashboard();
        } else if (spaceInfo?.selectedOperation === 'BRAND') {
            getBrandManualDashboard();
        }
    }, [manualPageable.rowPerPage, manualPageable.page, spaceInfo]);

    const onDeviceShowAllClick = () => {
        shopInfoState?.type === 'CAFE' && navigate(RouteUrl.cafe.setting.device.url);
        shopInfoState?.type === 'CAFETERIA' && navigate(RouteUrl.dining.setting.device.url);
    }

    const goToDeviceDetail = (id?: number) => {
        shopInfoState?.type === 'CAFE' && navigate(`${RouteUrl.cafe.setting.device.url}/${id}`, {state: {dashboardDetail: true}});
        shopInfoState?.type === 'CAFETERIA' && navigate(`${RouteUrl.dining.setting.device.url}/${id}`, {state: {dashboardDetail: true}});
    }


    const getCafeManualDashboard = () => {
        dashboardRepository.getCafeManualDashboard({
            page: manualPageable.page
        });
    }
    const getCafeOperationDashboard = () => {
        dashboardRepository.getCafeOperationDashboard({
            shopId: shopInfoState?.id,
            opDate: dayjs().format(DATE_FORMAT_DASH)
        });
    }

    const getCafeNoticeDashboard = () => {
        dashboardRepository.getCafeNoticeDashboard({
            brandCode: shopInfoState?.brandCode,
            shopId: shopInfoState?.id,
            page: noticePageable.page
        });
    }

    const startTime = useMemo(() => {
        if (shopInfoState?.shopSalesPlan?.autoOnOffYn) {
            return dayjs(shopInfoState?.shopSalesPlan?.autoOpenTime, TIME_MIN_FORMAT).format(TIME_MIN_FORMAT);
        } else {
            // return dayjs(shopInfoState?.shopSalesPlan?.openTime).format(TIME_MIN_FORMAT) ? dayjs(shopInfoState?.shopSalesPlan?.openTime).format(TIME_MIN_FORMAT) : '08:00';
            return shopInfoState?.shopSalesPlan?.openTime && dayjs(shopInfoState?.shopSalesPlan?.openTime).isValid() ? dayjs(shopInfoState?.shopSalesPlan?.openTime).format(TIME_MIN_FORMAT) : '08:00';
        }
    }, [shopInfoState]);

    const endTime = useMemo(() => {
        if (shopInfoState?.shopSalesPlan?.autoOnOffYn) {
            return dayjs(shopInfoState?.shopSalesPlan?.autoCloseTime, TIME_MIN_FORMAT).format(TIME_MIN_FORMAT);
        } else {
            return '23:59';
        }
    }, [shopInfoState]);

    const getCafeOrderByTimeDashboard = (orderByTimeDiff: number) => {
        console.log(shopInfoState?.shopSalesPlan?.openTime);

        dashboardRepository.getCafeOrderByTimeDashboard({
            shopId: shopInfoState?.id,
            startOpDate: dayjs().format(DATE_FORMAT_DASH),
            endOpDate: dayjs().format(DATE_FORMAT_DASH),
            timeUnit: orderByTimeDiff,
            startTime: startTime,
            endTime: endTime
        })
    }

    const getCafeDeviceDashboard = () => {
        dashboardRepository.getCafeDeviceDashboard({shopId: shopInfoState.id});
    }

    const getDiningManualDashboard = () => {
        dashboardRepository.getDiningManualDashboard({
            page: manualPageable.page
        });
    }

    const getDiningNoticeDashboard = () => {
        dashboardRepository.getDiningNoticeDashboard({
            brandCode: shopInfoState?.brandCode,
            shopId: shopInfoState?.id,
            page: noticePageable.page
        });
    }

    const getDiningDeviceDashboard = () => {
        dashboardRepository.getDiningDeviceDashboard({shopId: shopInfoState.id});
    }

    const getDiningOrderByTimeDashboard = (orderByTimeDiff: number) => {
        dashboardRepository.getDiningOrderByTimeDashboard({
            shopId: shopInfoState?.id,
            startOpDate: dayjs().format(DATE_FORMAT_DASH),
            endOpDate: dayjs().format(DATE_FORMAT_DASH),
            timeUnit: orderByTimeDiff,
            startTime: startTime,
            endTime: endTime
        })
    }

    const getDiningShopStatusDashboard = () => {
        dashboardRepository.getDiningShopStatusDashboard({
            shopId: shopInfoState?.id,
            opDate: dayjs().format(DATE_FORMAT_DASH)
        })
    }

    const getDiningOperationDashboard = () => {
        dashboardRepository.getDiningOperationDashboard({
            shopId: shopInfoState?.id,
            currentDate: dayjs().format(DATE_FORMAT_DASH),
            currentTime: dayjs().format(TIME_MIN_FORMAT)
        })
    }

    const getDiningCourseStatusDashboard = () => {
        dashboardRepository.getDiningCourseStatusDashboard({
            shopId: shopInfoState?.id,
            currentDate: dayjs().format(DATE_FORMAT_DASH),
            currentTime: dayjs().format(TIME_MIN_FORMAT)
        })
    }

    const onCourseCongestionEdit = (mealCourseId?: number, congestionType?: _SERVER_CONGESTION_TYPE) => {
        dashboardRepository.onCourseCongestionEdit({mealCourseId, congestionType});
    }

    const onMealSoldOutEdit = (mealId: number, soldOutYn: boolean) => {
        dashboardRepository.onMealSoldOutEdit({mealId, soldOutYn});
    }


    const getBrandUsageShopDashboard = () => {
        dashboardRepository.getBrandUsageShopDashboard({
            opDate: dayjs().format(DATE_FORMAT_DASH),
            brandCode: brandInfoState?.code
        })
    }

    const getBrandUsageCompanyDashboard = () => {
        dashboardRepository.getBrandUsageCompanyDashboard({
            opDate: dayjs().format(DATE_FORMAT_DASH),
            brandCode: brandInfoState?.code
        })
    }

    const getBrandNoticeDashboard = () => {
        dashboardRepository.getBrandNoticeDashboard({
            brandCode: brandInfoState?.code,
            page: noticePageable.page
        })
    }

    const getBrandManualDashboard = () => {
        dashboardRepository.getBrandManualDashboard({
            page: manualPageable.page
        });
    }

    const getBrandRegisteredDashboard = () => {
        dashboardRepository.getBrandRegisteredDashboard({
            brandCode: brandInfoState?.code
        });
    }

    const getBrandRegisteredGraphDashboard = () => {
        dashboardRepository.getBrandRegisteredGraphDashboard({
            brandCode: brandInfoState?.code
        });
    }

    return {
        shopInfoState,
        brandInfoState,

        cafeOperationData,
        cafeManualData,
        cafeNoticeData,
        cafeOrderByTimeData,
        cafeDeviceData,
        getCafeOperationDashboard,
        getCafeManualDashboard,
        getCafeNoticeDashboard,
        getCafeOrderByTimeDashboard,
        getCafeDeviceDashboard,

        diningManualData,
        diningNoticeData,
        diningDeviceData,
        diningOrderByTimeData,
        diningShopStatusData,
        diningOperationData,
        diningCourseStatusData,
        getDiningManualDashboard,
        getDiningNoticeDashboard,
        getDiningDeviceDashboard,
        getDiningOrderByTimeDashboard,
        getDiningShopStatusDashboard,
        getDiningOperationDashboard,
        getDiningCourseStatusDashboard,
        onCourseCongestionEdit,
        onMealSoldOutEdit,

        brandUsageShopData,
        brandUsageCompanyData,
        brandNoticeData,
        brandManualData,
        brandRegisteredData,
        brandRegisteredGraphData,
        getBrandUsageShopDashboard,
        getBrandUsageCompanyDashboard,
        getBrandNoticeDashboard,
        getBrandManualDashboard,
        getBrandRegisteredDashboard,
        getBrandRegisteredGraphDashboard,

        noticePageable,
        manualPageable,

        onDeviceShowAllClick,
        goToDeviceDetail
    }
}